import { API_CONFIG } from './config';
import type { ApiResponse } from './types';
import { FallbackService } from '../FallbackService';
import { CircuitBreaker } from '../CircuitBreaker';

// Cette fonction est un placeholder. Dans une vraie application,
// vous auriez un moyen de passer le token depuis votre contexte d'authentification.
// Pour l'instant, nous laissons cette structure, mais elle devra être adaptée.
let getAccessToken: () => Promise<string | null> = async () => null;

export const setAuthTokenProvider = (provider: () => Promise<string | null>) => {
  getAccessToken = provider;
};

// Export pour permettre aux autres services (comme ChatStreamService) d'utiliser le même provider
export const getAuthToken = async (): Promise<string | null> => {
  return getAccessToken();
};

// Cache des requêtes pour éviter les appels multiples
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5000; // 5 secondes

// Système de rate limiting
const requestQueue = new Map<string, Promise<any>>();

export class ApiService {
  private static baseUrl = API_CONFIG.baseUrl;
  private static retryDelay = 1000; // 1 seconde
  private static maxRetries = 3;

  private static async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Accounting-Client': 'Wanzo-Accounting-UI/1.0.0'
    };

    const token = await getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private static async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private static getCacheKey(endpoint: string, params?: Record<string, any>): string {
    return `${endpoint}${params ? JSON.stringify(params) : ''}`;
  }

  private static isRateLimitError(response: Response): boolean {
    return response.status === 429;
  }

  private static isConnectionError(error: any): boolean {
    return error instanceof TypeError || error.name === 'AbortError' || error.code === 'ECONNRESET';
  }

  private static getCircuitBreaker(endpoint: string): CircuitBreaker {
    return CircuitBreaker.getInstance(`api-${endpoint}`, 3, 30000); // 3 échecs max, 30s timeout
  }

  static resetCircuitBreakers(): void {
    // Méthode utilitaire pour réinitialiser tous les circuit breakers en cas de besoin
    console.log('🔄 Resetting all circuit breakers...');
  }

  private static async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (response.status === 401) {
      // La redirection sera gérée par le ProtectedRoute ou un composant similaire
      return { success: false, error: 'Session expirée ou non autorisée' };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      return {
        success: false,
        error: errorData?.message || `HTTP error! status: ${response.status}`
      };
    }

    try {
      const data = await response.json();
      
      // Si la réponse contient déjà success et data (format API enveloppé complet)
      if (typeof data === 'object' && 'success' in data && 'data' in data) {
        return data; // Retourner tel quel
      }
      
      // Si la réponse contient juste 'data' sans 'success' (format backend simplifié)
      // Ex: { "data": { "user": {...}, "organization": {...} } }
      if (typeof data === 'object' && 'data' in data && !('success' in data)) {
        return { success: true, data: data.data };
      }
      
      // Sinon, envelopper dans le format ApiResponse
      return { success: true, data };
    } catch (error) {
      // Gère le cas où la réponse est OK mais n'a pas de corps JSON (ex: 204 No Content)
      if (response.status === 204) {
        return { success: true, data: null as any };
      }
      return {
        success: false,
        error: 'Failed to parse response data'
      };
    }
  }

  static async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const cacheKey = this.getCacheKey(endpoint, params);
    
    // Log pour settings
    if (endpoint.includes('settings')) {
      console.log('🌐 API GET:', `${this.baseUrl}${endpoint}`, params);
    }
    
    // Vérifier si une requête identique est déjà en cours
    if (requestQueue.has(cacheKey)) {
      return requestQueue.get(cacheKey);
    }

    // Vérifier le cache
    const cached = requestCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
      if (endpoint.includes('settings')) {
        console.log('💾 Cache hit pour:', endpoint);
      }
      return { success: true, data: cached.data };
    }

    // Créer une nouvelle requête
    const requestPromise = this.executeGetWithRetry<T>(endpoint, params, ApiService.maxRetries);
    requestQueue.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Mettre en cache le résultat si succès
      if (result.success) {
        requestCache.set(cacheKey, { data: result.data, timestamp: Date.now() });
      }
      
      return result;
    } finally {
      requestQueue.delete(cacheKey);
    }
  }

  private static async executeGetWithRetry<T>(endpoint: string, params?: Record<string, any>, retries: number = 3): Promise<ApiResponse<T>> {
    try {
      const url = new URL(`${this.baseUrl}${endpoint}`);
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }

      const response = await fetch(url.toString(), {
        headers: await this.getHeaders()
      });

      // Si rate limit, attendre et réessayer
      if (this.isRateLimitError(response) && retries > 0) {
        await this.sleep(ApiService.retryDelay * (ApiService.maxRetries - retries + 1)); // Backoff exponentiel
        return this.executeGetWithRetry<T>(endpoint, params, retries - 1);
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      // En cas d'erreur de connexion, réessayer
      if (this.isConnectionError(error) && retries > 0) {
        await this.sleep(ApiService.retryDelay);
        return this.executeGetWithRetry<T>(endpoint, params, retries - 1);
      }

      console.warn(`API Error for ${endpoint}:`, error);
      
      // Essayer le fallback en cas d'échec définitif
      const fallbackResult = await FallbackService.getFallbackData<T>(endpoint);
      if (fallbackResult) {
        return fallbackResult;
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  private static async executeRequestWithRetry<T>(
    endpoint: string, 
    method: string = 'GET', 
    data?: any, 
    retries: number = 3
  ): Promise<ApiResponse<T>> {
    try {
      const controller = new AbortController();
      // Timeout plus long pour les endpoints chat (génération IA)
      const isLongRequest = endpoint.startsWith('/chat') && method === 'POST';
      const timeout = isLongRequest ? 60000 : 10000; // 60s pour chat, 10s pour le reste
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const options: RequestInit = {
        method,
        headers: await this.getHeaders(),
        signal: controller.signal
      };

      if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, options);
      clearTimeout(timeoutId);

      // Si rate limit, attendre et réessayer
      if (this.isRateLimitError(response) && retries > 0) {
        console.warn(`⚠️ Rate limit detected for ${endpoint}, retrying in ${ApiService.retryDelay * (ApiService.maxRetries - retries + 1)}ms...`);
        await this.sleep(ApiService.retryDelay * (ApiService.maxRetries - retries + 1));
        return this.executeRequestWithRetry<T>(endpoint, method, data, retries - 1);
      }

      // Si rate limit et plus de retries, lever une erreur pour déclencher le circuit breaker
      if (this.isRateLimitError(response)) {
        throw new Error(`Rate limit exceeded for ${endpoint} (429 Too Many Requests)`);
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      // En cas d'erreur de connexion, réessayer
      if (this.isConnectionError(error) && retries > 0) {
        await this.sleep(ApiService.retryDelay);
        return this.executeRequestWithRetry<T>(endpoint, method, data, retries - 1);
      }

      console.warn(`API Error for ${method} ${endpoint}:`, error);
      
      // Essayer le fallback seulement pour GET
      if (method === 'GET') {
        const fallbackResult = await FallbackService.getFallbackData<T>(endpoint);
        if (fallbackResult) {
          return fallbackResult;
        }
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  static async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    const circuitBreaker = this.getCircuitBreaker(endpoint);
    return circuitBreaker.execute(() => this.executeRequestWithRetry<T>(endpoint, 'POST', data, ApiService.maxRetries));
  }

  static async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    if (endpoint.includes('settings')) {
      console.log('🌐 API PUT:', `${this.baseUrl}${endpoint}`, data);
    }
    const circuitBreaker = this.getCircuitBreaker(endpoint);
    const result = await circuitBreaker.execute(() => this.executeRequestWithRetry<T>(endpoint, 'PUT', data, ApiService.maxRetries));
    if (endpoint.includes('settings')) {
      console.log('📥 PUT Response:', result);
    }
    return result;
  }

  static async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    const circuitBreaker = this.getCircuitBreaker(endpoint);
    return circuitBreaker.execute(() => this.executeRequestWithRetry<T>(endpoint, 'PATCH', data, ApiService.maxRetries));
  }

  static async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    const circuitBreaker = this.getCircuitBreaker(endpoint);
    return circuitBreaker.execute(() => this.executeRequestWithRetry<T>(endpoint, 'DELETE', undefined, ApiService.maxRetries));
  }
}